import { useMsal } from '@azure/msal-react';
import {
    Button,
    Checkbox,
    Combobox,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Input,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Option,
    tokens,
    Toolbar,
    ToolbarButton,
    ToolbarDivider,
} from '@fluentui/react-components';
import { ArrowUndo24Regular, Checkmark16Regular, ChevronDown16Regular, Dismiss16Regular, Dismiss24Regular, Edit24Regular, Save24Regular, Shield24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import type { IDataItem } from '../../models/IDataItem';
import type { ProjektArtItem } from '../../models/ProjektArtItem';
import type { ProjektInstitutItem } from '../../models/ProjektInstitutItem';
import type { ProjektItem } from '../../models/ProjektItem';
import type { ProjektmitarbeiterItem } from '../../models/ProjektmitarbeiterItem';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';
import { EckdatenOverviewFinancialData } from './EckdatenOverviewFinancialData';
import { EckdatenOverviewOrders } from './EckdatenOverviewOrders';
import { EckdatenOverviewProjectTime } from './EckdatenOverviewProjectTime';

type OverviewSectionKey = 'orders' | 'projectTime' | 'financialData' | 'reductionInLectures' | 'reports';

const OVERVIEW_SECTIONS: { key: OverviewSectionKey; label: string; placeholder: string }[] = [
    { key: 'orders', label: 'Orders', placeholder: 'Placeholder' },
    { key: 'projectTime', label: 'Project time', placeholder: 'Placeholder card with title and description' },
    { key: 'financialData', label: 'Financial data', placeholder: 'Placeholder' },
    { key: 'reductionInLectures', label: 'Reduction in lectures', placeholder: 'Placeholder card with title and description' },
    { key: 'reports', label: 'Reports', placeholder: 'Placeholder card with title and description' },
];

const OVERVIEW_SECTIONS_STORAGE_KEY = 'eckdatenOverview.visibleSections';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
    },
    layout: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalL,
    },
    mainColumn: {
        flex: 1,
        minWidth: 0,
    },
    sidePanel: {
        position: 'sticky',
        top: tokens.spacingVerticalL,
        flexShrink: 0,
        width: '200px',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        padding: tokens.spacingHorizontalM,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    sidePanelTitle: {
        fontWeight: 600,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
    },
    sectionPlaceholder: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
        fontSize: tokens.fontSizeBase300,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalL,
    },
    headerTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorNeutralForeground1,
    },
    categorySection: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
        paddingBottom: tokens.spacingVerticalL,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    categoryHeader: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorBrandForeground1,
        paddingTop: '2px',
    },
    fieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
    },
    fieldBox: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalM,
        alignItems: 'start',
        paddingBottom: 0,
    },
    fieldLabel: {
        fontWeight: 600,
        fontSize: '11px',
        color: tokens.colorNeutralForeground2,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        paddingTop: '4px',
    },
    fieldValue: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        wordWrap: 'break-word',
    },
    emptyValue: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
    },
    inputField: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
        height: '32px',
    },
    commandsSection: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
        paddingBottom: tokens.spacingVerticalL,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    commandsHeader: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorBrandForeground1,
        paddingTop: '2px',
    },
    commandsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    commandItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalM,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    commandItemLast: {
        borderBottom: 'none',
    },
    commandTitle: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: 600,
        color: tokens.colorNeutralForeground1,
    },
    commandDescription: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
        marginBottom: tokens.spacingVerticalS,
    },
    commandInstructions: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
    },
    commandButtons: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
    },
    checklist: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        maxWidth: '420px',
    },
    checklistItem: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
    },
    checklistIconComplete: {
        color: tokens.colorPaletteGreenForeground1,
        flexShrink: 0,
    },
    checklistIconMissing: {
        color: tokens.colorPaletteRedForeground1,
        flexShrink: 0,
    },
    statusContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalM,
    },
    statusItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    statusLabel: {
        fontSize: '11px',
        fontWeight: 600,
        color: tokens.colorNeutralForeground2,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
        borderRadius: tokens.borderRadiusMedium,
        fontSize: tokens.fontSizeBase300,
        fontWeight: 600,
        width: 'fit-content',
    },
    statusApproved: {
        backgroundColor: tokens.colorPaletteGreenBackground3,
        color: tokens.colorPaletteGreenForeground2,
    },
    statusPending: {
        backgroundColor: tokens.colorPaletteYellowBackground3,
        color: tokens.colorPaletteYellowForeground2,
    },
    statusNotSent: {
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground2,
    },
    approvalDateInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        padding: tokens.spacingHorizontalM,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
        border: `2px solid ${tokens.colorPaletteGreenBorder1}`,
        minHeight: '60px',
        justifyContent: 'center',
        maxWidth: '200px',
    },
    approvalDateLabel: {
        fontSize: '11px',
        fontWeight: 700,
        color: tokens.colorPaletteGreenForeground1,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    approvalDateValue: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorPaletteGreenForeground1,
        fontWeight: 600,
        textAlign: 'center',
    },
    approvalStepsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalS,
        marginTop: tokens.spacingVerticalS,
        alignItems: 'center',
    },
    approvalStep: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: tokens.spacingVerticalXS,
        maxWidth: '200px',
        position: 'relative',
    },
    approvalStepCard: {
        width: '100%',
        padding: tokens.spacingHorizontalS,
        borderRadius: tokens.borderRadiusMedium,
        border: `2px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        minHeight: '45px',
    },
    approvalStepCardApproved: {
        border: `2px solid ${tokens.colorPaletteGreenBorder1}`,
    },
    approvalStepCardPending: {
        border: `2px solid ${tokens.colorPaletteYellowBorder1}`,
    },
    approvalStepCardNotSent: {
        border: `2px solid ${tokens.colorNeutralStroke2}`,
    },
    approvalStepTitle: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: 600,
        color: tokens.colorNeutralForeground1,
        textAlign: 'center',
    },
    approvalStepStatus: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: 500,
        color: tokens.colorNeutralForeground1,
        textAlign: 'center',
    },
    approvalStepStatusApproved: {
        color: tokens.colorPaletteGreenForeground1,
        fontWeight: 600,
    },
    approvalStepStatusPending: {
        color: tokens.colorPaletteYellowForeground1,
        fontWeight: 600,
    },
    approvalStepStatusNotSent: {
        color: tokens.colorNeutralForeground2,
        fontWeight: 600,
    },
    stepNumber: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: tokens.colorNeutralBackground3,
        fontSize: tokens.fontSizeBase300,
        fontWeight: 700,
        color: tokens.colorNeutralForeground1,
        flexShrink: 0,
    },
    stepNumberApproved: {
        backgroundColor: tokens.colorPaletteGreenBorder1,
        color: tokens.colorNeutralForeground4,
    },
    stepNumberPending: {
        backgroundColor: tokens.colorPaletteYellowBorder1,
        color: tokens.colorNeutralForeground1,
    },
    stepNumberNotSent: {
        backgroundColor: tokens.colorNeutralStroke2,
        color: tokens.colorNeutralForeground3,
    },
    stepConnector: {
        height: '2px',
        width: '24px',
        backgroundColor: tokens.colorNeutralStroke2,
        flexShrink: 0,
    },
});

interface EckdatenOverviewProps {
    project: ProjektItem | null;
}

interface FieldDefinition {
    label: string;
    key: keyof ProjektItem | string;
    permission: 'r' | 'rw' | '-';
    isSelect?: boolean;
    selectType?: 'status' | 'institute' | 'projektart' | 'mitarbeiter';
    editable?: boolean;
}

interface DataGroup {
    title: string;
    fields: FieldDefinition[];
}

interface EditedValues {
    [key: string]: string | number | undefined;
    projektleiterId?: number;
}

const initializeEditedValues = (project: ProjektItem | null): EditedValues => {
    if (!project) return {};
    return {
        projektStatus: String(project.projektStatus || ''),
        projektleiterName: project.projektleiterName || '',
        projektleiterId: project.projektleiterID || undefined,
        projektID: String(project.projektID || ''),
        kostentraegerNummer: project.kostentraegerNummer || '',
        departmentID: String(project.departmentID || ''),
        projektart: String(project.projektartID || ''),
        projektKurzbeschreibung: project.projektKurzbeschreibung || '',
        projektauftraggeber: project.projektauftraggeber || '',
    };
};


const EckdatenOverview: React.FC<EckdatenOverviewProps> = ({ project: _project }) => {
    const styles = useStyles();
    const { selectedRole } = useConfigStore((state) => state.roleConfig);
    const { accounts, instance } = useMsal();
    const [isEditing, setIsEditing] = useState(false);
    const [visibleSections, setVisibleSections] = useState<OverviewSectionKey[]>(() => {
        try {
            const parsed: unknown = JSON.parse(localStorage.getItem(OVERVIEW_SECTIONS_STORAGE_KEY) ?? '[]');
            if (!Array.isArray(parsed)) return [];
            return OVERVIEW_SECTIONS.map((s) => s.key).filter((key) => parsed.includes(key));
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem(OVERVIEW_SECTIONS_STORAGE_KEY, JSON.stringify(visibleSections));
    }, [visibleSections]);

    const toggleSection = (key: OverviewSectionKey, checked: boolean) => {
        setVisibleSections((prev) => (checked ? [...prev.filter((k) => k !== key), key] : prev.filter((k) => k !== key)));
    };
    const [editedValues, setEditedValues] = useState<EditedValues>(() => initializeEditedValues(_project));
    const [statusOptions, setStatusOptions] = useState<IDataItem[]>([]);
    const [instituteOptions, setInstituteOptions] = useState<ProjektInstitutItem[]>([]);
    const [projektArtOptions, setProjektArtOptions] = useState<ProjektArtItem[]>([]);
    const [mitarbeiterOptions, setMitarbeiterOptions] = useState<ProjektmitarbeiterItem[]>([]);
    const [memberSearchResults, setMemberSearchResults] = useState<ProjektmitarbeiterItem[]>([]);
    const [memberSearchTimeout, setMemberSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [optionsLoaded, setOptionsLoaded] = useState(false);
    const [isApprovingOffer, setIsApprovingOffer] = useState(false);
    const [isTerminatingOperative, setIsTerminatingOperative] = useState(false);
    const [isTerminatingFinancial, setIsTerminatingFinancial] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isCommandDialogOpen, setIsCommandDialogOpen] = useState(false);
    const [dialogConfig, setDialogConfig] = useState<{
        title: string;
        message: string;
        actionType: 'approveOffer' | 'operativeEnd' | 'financialEnd' | 'setStatusLaufend' | 'setStatusOperativBeendet' | 'archiveProject' | 'unarchiveProject';
    } | null>(null);
    const [isSettingStatus, setIsSettingStatus] = useState(false);
    const [isArchivingProject, setIsArchivingProject] = useState(false);
    const [isUnarchivingProject, setIsUnarchivingProject] = useState(false);
    const { refreshProject } = useProject();

    const isAdmin = useMemo(() => selectedRole === 'Administratoren', [selectedRole]);
    const isProjektleiter = useMemo(() => _project?.isBenutzerProjektLeiter, [_project?.isBenutzerProjektLeiter]);
    const isControlling = useMemo(() => selectedRole === 'Controlling', [selectedRole]);
    const isForschungsservice = useMemo(() => selectedRole === 'Forschungsservice', [selectedRole]);
    const canSeeCommands = isProjektleiter === true || isAdmin || isControlling;
    const canArchiveProject = _project?.projektStatus === 5 && (_project?.storniertArchiviert === false) && (isControlling || isAdmin || isForschungsservice || isProjektleiter === true);
    const canUnarchiveProject = _project?.storniertArchiviert === true && (isControlling || isAdmin || isForschungsservice || isProjektleiter === true);
    const showCommandsSection = (canSeeCommands && (_project?.projektStatus === 2 || _project?.projektStatus === 3 || _project?.projektStatus === 4)) || canArchiveProject || canUnarchiveProject;

    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    const fetchSelectOptions = useCallback(async () => {
        if (!dataService || optionsLoaded) return;

        try {
            const [statuses, institutes, arts, mitarbeiter] = await Promise.all([
                dataService.getProjektStatusItems(),
                dataService.getProjektInstitute(),
                dataService.getProjektArten(_project?.departmentID || 0),
                dataService.getMitarbeiter(),
            ]);
            setStatusOptions(statuses);
            setInstituteOptions(institutes);
            setProjektArtOptions(arts);
            setMitarbeiterOptions(mitarbeiter);
            setOptionsLoaded(true);
        } catch (error) {
            console.error('Error fetching select options:', error);
        }
    }, [dataService, optionsLoaded, _project?.departmentID]);

    useEffect(() => {
        if (isEditing && !optionsLoaded && dataService) {
            fetchSelectOptions();
            // Initialize member search results with all mitarbeiter options
            setMemberSearchResults(mitarbeiterOptions);
        }
    }, [isEditing, optionsLoaded, dataService, fetchSelectOptions, mitarbeiterOptions]);

    useEffect(() => {
        console.log('Initializing edited values with project:', _project);
        setEditedValues(initializeEditedValues(_project));
    }, [_project]);

    const handleFieldChange = (key: string, value: string) => {
        setEditedValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSave = async () => {
        if (!_project || !dataService) return;

        setIsSaving(true);
        try {
            // Merge edited values into the project
            const updatedProject: ProjektItem = {
                ...(_project as ProjektItem),
                projektStatus: parseInt(editedValues.projektStatus as string) || _project.projektStatus,
                projektleiterName: editedValues.projektleiterName as string,
                projektleiterID: parseInt(String(editedValues.projektleiterId)) || _project.projektleiterID,
                kostentraegerNummer: editedValues.kostentraegerNummer as string,
                departmentID: parseInt(editedValues.departmentID as string) || _project.departmentID,
                projektartID: parseInt(editedValues.projektart as string) || _project.projektartID,
                projektKurzbeschreibung: editedValues.projektKurzbeschreibung as string,
                projektauftraggeber: editedValues.projektauftraggeber as string,
            };

            // Call API to save
            const result = await dataService.updateProjekt(updatedProject);

            if (result.success) {
                if (refreshProject) {
                    await refreshProject();
                }
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 3000);
                setIsEditing(false);
            } else {
                // Show error message
                setShowSuccessMessage(false);
                setErrorMessage(result.message);
                setTimeout(() => {
                    setErrorMessage(null);
                }, 5000);
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error saving changes. Please try again.');
            setTimeout(() => {
                setErrorMessage(null);
            }, 5000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (isSaving) return;
        setEditedValues(initializeEditedValues(_project));
        setIsEditing(false);
        setMemberSearchResults(mitarbeiterOptions);
        if (memberSearchTimeout) clearTimeout(memberSearchTimeout);
    };

    // Index order matches radySetForOfferApproval from the backend
    const offerApprovalChecklist = useMemo(() => {
        const flags = _project?.readySetForOfferApproval ?? [];
        return [
            { key: 'projectDataFields', label: 'Project Data Fields', isComplete: flags[0] === true },
            { key: 'riskmanagement', label: 'Riskmanagement (min. 3)', isComplete: flags[1] === true },
            { key: 'proposalApproval', label: 'Approval of Proposal by Head of Institute/Department', isComplete: flags[2] === true },
            { key: 'calculationApproval', label: 'Approval of Calculation by CO', isComplete: flags[3] === true },
        ];
    }, [_project]);
    const isOfferApprovalReady = offerApprovalChecklist.every(item => item.isComplete);

    const handleApproveOffer = async () => {
        if (!_project || !dataService || !isOfferApprovalReady) return;

        try {
            setIsApprovingOffer(true);
            await dataService.freigabeAngebot(_project.projektID);
            // Refresh the project data
            if (refreshProject) {
                await refreshProject();
            }
            setErrorMessage(null);
            setShowSuccessMessage(true);
            setIsCommandDialogOpen(false);
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 5000);
        } catch (error) {
            console.error('Error approving offer:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error requesting offer approval. Please try again.');
            setTimeout(() => {
                setErrorMessage(null);
            }, 5000);
        } finally {
            setIsApprovingOffer(false);
        }
    };

    const openApproveOfferDialog = () => {
        setDialogConfig({
            title: 'Confirm Offer Approval',
            message: 'Are you sure you want to request approval from executive management for this project offer? This action cannot be easily reversed.',
            actionType: 'approveOffer',
        });
        setIsCommandDialogOpen(true);
    };

    const openSetStatusDialog = (actionType: 'setStatusLaufend' | 'setStatusOperativBeendet') => {
        const statusName = actionType === 'setStatusLaufend' ? 'Laufend' : 'Operativ Beendet';
        setDialogConfig({
            title: `Set Status Back to ${statusName}`,
            message: `Are you sure you want to set the project status back to "${statusName}"?`,
            actionType,
        });
        setIsCommandDialogOpen(true);
    };

    const confirmSetStatus = async (status: number) => {
        if (!_project || !dataService) return;

        try {
            setIsSettingStatus(true);
            await dataService.setStatusOfProject(_project.projektID, status);
            if (refreshProject) {
                await refreshProject();
            }
            setErrorMessage(null);
            setShowSuccessMessage(true);
            setIsCommandDialogOpen(false);
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 5000);
        } catch (error) {
            console.error('Error setting project status:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error setting project status. Please try again.');
            setTimeout(() => {
                setErrorMessage(null);
            }, 5000);
        } finally {
            setIsSettingStatus(false);
        }
    };

    const openOperativeEndDialog = () => {
        setDialogConfig({
            title: 'Confirm Operational End',
            message: 'Are you sure you want to terminate the project operationally? This action marks the end of active project work and cannot be easily reversed.',
            actionType: 'operativeEnd',
        });
        setIsCommandDialogOpen(true);
    };

    const confirmOperativeEnd = async () => {
        if (!_project || !dataService) return;

        try {
            setIsTerminatingOperative(true);
            await dataService.projektOperativBeenden(_project.projektID);
            // Refresh the project data
            if (refreshProject) {
                await refreshProject();
            }
            setErrorMessage(null);
            setShowSuccessMessage(true);
            setIsCommandDialogOpen(false);
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 5000);
        } catch (error) {
            console.error('Error terminating project operationally:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error terminating project operationally. Please try again.');
            setTimeout(() => {
                setErrorMessage(null);
            }, 5000);
        } finally {
            setIsTerminatingOperative(false);
        }
    };

    const openFinancialEndDialog = () => {
        setDialogConfig({
            title: 'Confirm Financial End',
            message: 'Are you sure you want to commercially terminate the project? This action finalizes all financial aspects of the project and cannot be easily reversed.',
            actionType: 'financialEnd',
        });
        setIsCommandDialogOpen(true);
    };

    const confirmFinancialEnd = async () => {
        if (!_project || !dataService) return;

        try {
            setIsTerminatingFinancial(true);
            await dataService.projektKaufmannischBeenden(_project.projektID);
            // Refresh the project data
            if (refreshProject) {
                await refreshProject();
            }
            setErrorMessage(null);
            setShowSuccessMessage(true);
            setIsCommandDialogOpen(false);
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 5000);
        } catch (error) {
            console.error('Error terminating project financially:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error terminating project financially. Please try again.');
            setTimeout(() => {
                setErrorMessage(null);
            }, 5000);
        } finally {
            setIsTerminatingFinancial(false);
        }
    };

    const openArchiveProjectDialog = () => {
        setDialogConfig({
            title: 'Confirm Project Archiving',
            message: 'Are you sure you want to archive this project? It will be marked as archived and cancelled.',
            actionType: 'archiveProject',
        });
        setIsCommandDialogOpen(true);
    };

    const confirmArchiveProject = async () => {
        if (!_project || !dataService) return;

        try {
            setIsArchivingProject(true);
            const result = await dataService.updateProjekt({
                ...(_project as ProjektItem),
                archived: new Date(),
                storniertArchiviert: true,
            });
            if (!result.success) {
                throw new Error(result.message);
            }
            if (refreshProject) {
                await refreshProject();
            }
            setErrorMessage(null);
            setShowSuccessMessage(true);
            setIsCommandDialogOpen(false);
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 5000);
        } catch (error) {
            console.error('Error archiving project:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error archiving project. Please try again.');
            setTimeout(() => {
                setErrorMessage(null);
            }, 5000);
        } finally {
            setIsArchivingProject(false);
        }
    };

    const openUnarchiveProjectDialog = () => {
        setDialogConfig({
            title: 'Confirm Project Unarchiving',
            message: 'Are you sure you want to unarchive this project? It will no longer be marked as archived and cancelled.',
            actionType: 'unarchiveProject',
        });
        setIsCommandDialogOpen(true);
    };

    const confirmUnarchiveProject = async () => {
        if (!_project || !dataService) return;

        try {
            setIsUnarchivingProject(true);
            const result = await dataService.updateProjekt({
                ...(_project as ProjektItem),
                archived: null,
                storniertArchiviert: false,
            });
            if (!result.success) {
                throw new Error(result.message);
            }
            if (refreshProject) {
                await refreshProject();
            }
            setErrorMessage(null);
            setShowSuccessMessage(true);
            setIsCommandDialogOpen(false);
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 5000);
        } catch (error) {
            console.error('Error unarchiving project:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error unarchiving project. Please try again.');
            setTimeout(() => {
                setErrorMessage(null);
            }, 5000);
        } finally {
            setIsUnarchivingProject(false);
        }
    };

    // Handle member search for Project Manager
    const handleMemberSearch = useCallback((searchTerm: string) => {
        if (memberSearchTimeout) clearTimeout(memberSearchTimeout);

        if (searchTerm.length < 2) {
            setMemberSearchResults(mitarbeiterOptions);
            return;
        }

        const timeout = setTimeout(() => {
            const lowerSearchTerm = searchTerm.toLowerCase();
            const filtered = mitarbeiterOptions.filter((member) => {
                const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                return fullName.toLowerCase().includes(lowerSearchTerm);
            });
            setMemberSearchResults(filtered);
        }, 5);

        setMemberSearchTimeout(timeout);
    }, [mitarbeiterOptions, memberSearchTimeout]);

    // Define fields for Overview tab based on CSV mapping
    const dataGroups: DataGroup[] = [
        {
            title: 'Project',
            fields: [
                { label: 'Project Status', key: 'projektStatus', permission: 'r', isSelect: true, selectType: 'status', editable: true },
                { label: 'Project Manager', key: 'projektleiterName', permission: 'r', isSelect: true, selectType: 'mitarbeiter', editable: true },
                { label: 'Project ID', key: 'projektID', permission: 'r', editable: false },
                { label: 'Cost Carrier Number', key: 'kostentraegerNummer', permission: 'r', editable: true },
                { label: 'Institute/Department', key: 'departmentID', permission: 'r', isSelect: true, selectType: 'institute', editable: true },
                { label: 'Project Type', key: 'projektart', permission: 'r', isSelect: true, selectType: 'projektart', editable: true },
            ],
        },
        {
            title: 'Project Data',
            fields: [
                { label: 'Project Description', key: 'projektKurzbeschreibung', permission: 'r', editable: true },
            ],
        },
        {
            title: 'Project Sponsor',
            fields: [
                { label: 'Sponsor Name Line 1', key: 'projektauftraggeberBezeichnung1', permission: 'r', editable: false },
                { label: 'Sponsor Name Line 2', key: 'projektauftraggeberBezeichnung2', permission: 'r', editable: false },
            ],
        },
    ];

    const renderDisplayField = (field: FieldDefinition): React.ReactNode => {
        let displayValue = '';

        if (!_project) {
            displayValue = '-';
        } else {
            // Handle select fields - show text values instead of IDs
            if (field.isSelect) {
                switch (field.selectType) {
                    case 'status':
                        displayValue = _project.projektstatusInternBezeichnung || '-';
                        break;
                    case 'institute':
                        displayValue = _project.department || '-';
                        break;
                    case 'projektart':
                        displayValue = _project.projektart || '-';
                        break;
                    case 'mitarbeiter':
                        displayValue = _project.projektleiterName || '-';
                        break;
                    default:
                        displayValue = '-';
                }
            } else {
                const key = field.key as keyof ProjektItem;
                const value = _project[key];
                displayValue = value ? String(value) : '-';
            }
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{field.label}</div>
                <div className={styles.fieldValue}>
                    {displayValue === '-' ? <span className={styles.emptyValue}>-</span> : displayValue}
                </div>
            </div>
        );
    };

    const renderEditField = (field: FieldDefinition): React.ReactNode => {
        const value = String(editedValues[field.key] || '');

        if (field.isSelect && field.selectType) {
            // For Project Manager, use searchable Combobox
            if (field.selectType === 'mitarbeiter') {
                return (
                    <div className={styles.fieldBox}>
                        <div className={styles.fieldLabel}>{field.label}</div>
                        <Combobox
                            placeholder="Enter at least 2 characters..."
                            value={value}
                            onOptionSelect={(_, option) => {
                                const member = memberSearchResults.find((m) => m.personalID === parseInt(option.optionValue || '0'));
                                if (member) {
                                    const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                    handleFieldChange(String(field.key), fullName);
                                    handleFieldChange('projektleiterId', String(member.personalID));
                                }
                            }}
                            onChange={(ev) => {
                                const searchValue = (ev.target as HTMLInputElement).value;
                                handleFieldChange(String(field.key), searchValue);
                                handleMemberSearch(searchValue);
                            }}
                            appearance="outline"
                        >
                            {value.length > 2 &&
                                memberSearchResults.map((member) => {
                                    const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                    return (
                                        <Option
                                            key={member.personalID}
                                            value={member.personalID.toString()}
                                            text={fullName}
                                        >
                                            {fullName}
                                        </Option>
                                    );
                                })}
                        </Combobox>
                    </div>
                );
            }

            let options: Array<{ id: string | number; label: string }> = [];

            switch (field.selectType) {
                case 'status':
                    options = statusOptions.map((item) => ({ id: item.value, label: item.text }));
                    break;
                case 'institute':
                    options = instituteOptions.map((item) => ({ id: item.id, label: item.bezeichnung }));
                    break;
                case 'projektart':
                    options = projektArtOptions.map((item) => ({ id: item.projektartID, label: item.bezeichnung }));
                    break;
            }

            let selectedOption;
            if (field.selectType === 'status' || field.selectType === 'institute' || field.selectType === 'projektart') {
                selectedOption = options.find((opt) => opt.id === value || String(opt.id) === value);
            } else {
                selectedOption = options.find((opt) => opt.label === value);
            }
            const displayValue = selectedOption ? selectedOption.label : '';

            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <Combobox
                        value={displayValue}
                        onOptionSelect={(_, option) => {
                            const selected = options.find((opt) => opt.label === String(option.optionValue));
                            if (selected) {
                                handleFieldChange(String(field.key), String(selected.id));
                            }
                        }}
                        appearance="outline"
                    >
                        {options.map((opt) => {
                            const isSelected = String(opt.id) === String(value);
                            return (
                                <Option key={opt.id} value={opt.label} text={opt.label}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <span>{opt.label}</span>
                                        {isSelected && <Checkmark16Regular style={{ color: 'green' }} />}
                                    </div>
                                </Option>
                            );
                        })}
                    </Combobox>
                </div>
            );
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{field.label}</div>
                <Input
                    value={value}
                    onChange={(_ev, data) => {
                        handleFieldChange(String(field.key), data.value);
                    }}
                    className={styles.inputField}
                />
            </div>
        );
    };

    const renderField = (field: FieldDefinition) => {
        if (isAdmin && isEditing && field.editable !== false) {
            return renderEditField(field);
        }
        return renderDisplayField(field);
    };

    if (!_project) return null;

    return (
        <div className={styles.container}>
            <div className={styles.layout}>
                <div className={styles.mainColumn}>
                    {isSaving && (
                        <MessageBar>
                            <MessageBarBody>
                                <MessageBarTitle>Saving...</MessageBarTitle>
                                Please wait while your changes are being saved.
                            </MessageBarBody>
                        </MessageBar>
                    )}
                    {showSuccessMessage && (
                        <MessageBar intent="success">
                            <MessageBarBody>
                                <MessageBarTitle>Success</MessageBarTitle>
                                Project overview information saved successfully!
                            </MessageBarBody>
                        </MessageBar>
                    )}
                    {errorMessage && (
                        <MessageBar intent="error">
                            <MessageBarBody>
                                <MessageBarTitle>Error</MessageBarTitle>
                                {errorMessage}
                            </MessageBarBody>
                        </MessageBar>
                    )}
                    {/* Header with Edit Button */}
                    {isAdmin && (
                        <div className={styles.header}>
                            <div className={styles.headerTitle}>Project Overview</div>
                            <div>
                                {isEditing ? (
                                    <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
                                        <Button
                                            appearance="primary"
                                            icon={<Save24Regular />}
                                            onClick={handleSave}
                                            disabled={isSaving}
                                        >
                                            {isSaving ? 'Saving...' : 'Save'}
                                        </Button>
                                        <Button
                                            appearance="secondary"
                                            icon={<Dismiss24Regular />}
                                            onClick={handleCancel}
                                            disabled={isSaving}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                ) : (
                                    <Toolbar aria-label="Administrator actions">
                                        <Menu>
                                            <MenuTrigger disableButtonEnhancement>
                                                <ToolbarButton
                                                    icon={<Shield24Regular />}
                                                    disabled={isSettingStatus}
                                                >
                                                    Admin Actions <ChevronDown16Regular />
                                                </ToolbarButton>
                                            </MenuTrigger>
                                            <MenuPopover>
                                                <MenuList>
                                                    <MenuItem
                                                        icon={<ArrowUndo24Regular />}
                                                        onClick={() => openSetStatusDialog('setStatusLaufend')}
                                                        disabled={_project?.projektStatus !== 4}
                                                    >
                                                        Set Status Back to Laufend
                                                    </MenuItem>
                                                    <MenuItem
                                                        icon={<ArrowUndo24Regular />}
                                                        onClick={() => openSetStatusDialog('setStatusOperativBeendet')}
                                                        disabled={_project?.projektStatus !== 5}
                                                    >
                                                        Set Status Back to Operativ Beendet
                                                    </MenuItem>
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                        <ToolbarDivider />
                                        <ToolbarButton
                                            icon={<Edit24Regular />}
                                            onClick={() => setIsEditing(true)}
                                        >
                                            Edit
                                        </ToolbarButton>
                                    </Toolbar>
                                )}
                            </div>
                        </div>
                    )}

                    {dataGroups.map((group) => (
                        <div key={group.title} className={styles.categorySection}>
                            <div className={styles.categoryHeader}>{group.title}</div>
                            <div className={styles.fieldsContainer}>
                                {group.fields.map((field) => (
                                    <div key={field.label}>
                                        {renderField(field)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Commands Section - Conditional Visibility */}
                    {showCommandsSection && (
                        <div className={styles.commandsSection}>
                            <div className={styles.commandsHeader}>Commands</div>
                            <div className={styles.commandsContainer}>
                                {/* Offer Approval - Only when status is 2 (Offer) */}
                                {_project?.projektStatus === 2 && (
                                    <div className={styles.commandItem}>
                                        <div className={styles.commandTitle}>Offer Approval</div>
                                        <div className={styles.commandDescription}>
                                            Request approval from executive management for the project offer.
                                        </div>
                                        {!_project?.freigabeAngebotAngefordert ? (
                                            <>
                                                <div className={styles.commandInstructions}>
                                                    Please complete the following checklist before requesting approval.
                                                </div>
                                                <ul className={styles.checklist}>
                                                    {offerApprovalChecklist.map(item => (
                                                        <li key={item.key} className={styles.checklistItem}>
                                                            {item.isComplete ? (
                                                                <Checkmark16Regular className={styles.checklistIconComplete} />
                                                            ) : (
                                                                <Dismiss16Regular className={styles.checklistIconMissing} />
                                                            )}
                                                            <span>{item.label}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <div className={styles.commandButtons}>
                                                    <Button
                                                        appearance="primary"
                                                        onClick={openApproveOfferDialog}
                                                        disabled={isApprovingOffer || !isOfferApprovalReady}
                                                    >
                                                        {isApprovingOffer ? 'Requesting...' : 'Approval Executive Management'}
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className={styles.approvalStepsContainer}>
                                                {/* Step 1: FBL Approval */}
                                                <div className={styles.approvalStep}>
                                                    <div className={`${styles.stepNumber} ${_project?.freigabeFBL ? styles.stepNumberApproved : styles.stepNumberPending}`}>
                                                        1
                                                    </div>
                                                    <div className={`${styles.approvalStepCard} ${_project?.freigabeFBL ? styles.approvalStepCardApproved : styles.approvalStepCardPending}`}>
                                                        <div className={styles.approvalStepTitle}>FBL Approval</div>
                                                        <div className={`${styles.approvalStepStatus} ${_project?.freigabeFBL ? styles.approvalStepStatusApproved : styles.approvalStepStatusPending}`}>
                                                            {_project?.freigabeFBL ? '✓ Approved' : '⏳ Pending'}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Horizontal Connector */}
                                                <div className={styles.stepConnector} />

                                                {/* Step 2: GF Approval */}
                                                <div className={styles.approvalStep}>
                                                    <div className={`${styles.stepNumber} ${!_project?.freigabeFBL
                                                        ? styles.stepNumberNotSent
                                                        : _project?.freigabeGF
                                                            ? styles.stepNumberApproved
                                                            : styles.stepNumberPending
                                                        }`}>
                                                        2
                                                    </div>
                                                    <div className={`${styles.approvalStepCard} ${!_project?.freigabeFBL
                                                        ? styles.approvalStepCardNotSent
                                                        : _project?.freigabeGF
                                                            ? styles.approvalStepCardApproved
                                                            : styles.approvalStepCardPending
                                                        }`}>
                                                        <div className={styles.approvalStepTitle}>GF Approval</div>
                                                        <div className={`${styles.approvalStepStatus} ${!_project?.freigabeFBL
                                                            ? styles.approvalStepStatusNotSent
                                                            : _project?.freigabeGF
                                                                ? styles.approvalStepStatusApproved
                                                                : styles.approvalStepStatusPending
                                                            }`}>
                                                            {!_project?.freigabeFBL
                                                                ? '⊘ Not Sent (Waiting for FBL)'
                                                                : _project?.freigabeGF
                                                                    ? '✓ Approved'
                                                                    : '⏳ Pending'}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Connector after GF Approval */}
                                                {_project?.freigabeFBL && _project?.freigabeGF && (
                                                    <div className={styles.stepConnector} />
                                                )}

                                                {/* Approval Date */}
                                                {_project?.freigabeFBL && _project?.freigabeGF && (
                                                    <div className={styles.approvalStep}>
                                                        <div className={`${styles.stepNumber} ${styles.stepNumberApproved}`}>
                                                            ✓
                                                        </div>
                                                        <div className={`${styles.approvalStepCard} ${styles.approvalStepCardApproved}`}>
                                                            <div className={styles.approvalStepTitle}>Fully Approved</div>
                                                            <div className={styles.approvalDateValue}>
                                                                {_project?.datumFreigabeAngebot
                                                                    ? new Date(_project.datumFreigabeAngebot).toLocaleDateString('de-DE')
                                                                    : '-'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Operational End - Only when status is 3 (Laufend) and user has permission */}
                                {(_project?.projektStatus === 3 && (isProjektleiter || isControlling || isAdmin)) && (
                                    <div className={styles.commandItem}>
                                        <div className={styles.commandTitle}>Operational End</div>
                                        <div className={styles.commandDescription}>
                                            Terminate the project operationally. This marks the end of active project work.
                                        </div>
                                        <div className={styles.commandButtons}>
                                            <Button
                                                appearance="primary"
                                                onClick={openOperativeEndDialog}
                                                disabled={isTerminatingOperative}
                                            >
                                                {isTerminatingOperative ? 'Terminating...' : 'Terminate Operationally'}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Financial End - Only when status is 4 (operativ beendet) and user has permission */}
                                {(_project?.projektStatus === 4 && (isControlling || isAdmin)) && (
                                    <div className={styles.commandItem}>
                                        <div className={styles.commandTitle}>Financial End</div>
                                        <div className={styles.commandDescription}>
                                            Commercially terminate the project. This finalizes all financial aspects of the project.
                                        </div>
                                        <div className={styles.commandButtons}>
                                            <Button
                                                appearance="primary"
                                                onClick={openFinancialEndDialog}
                                                disabled={isTerminatingFinancial}
                                            >
                                                {isTerminatingFinancial ? 'Terminating...' : 'Terminate Commercially'}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Archive Project - Only when status is 5 and user has permission */}
                                {canArchiveProject && (
                                    <div className={styles.commandItem}>
                                        <div className={styles.commandTitle}>Archive Project</div>
                                        <div className={styles.commandDescription}>
                                            Archive the project. It will be marked as archived.
                                        </div>
                                        <div className={styles.commandButtons}>
                                            <Button
                                                appearance="primary"
                                                onClick={openArchiveProjectDialog}
                                                disabled={isArchivingProject}
                                            >
                                                {isArchivingProject ? 'Archiving...' : 'Archive Project'}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Unarchive Project - Only when project is archived and cancelled and user has permission */}
                                {canUnarchiveProject && (
                                    <div className={styles.commandItem}>
                                        <div className={styles.commandTitle}>Unarchive Project</div>
                                        <div className={styles.commandDescription}>
                                            Reopen the archived project. It will no longer be marked as archived.
                                        </div>
                                        <div className={styles.commandButtons}>
                                            <Button
                                                appearance="primary"
                                                onClick={openUnarchiveProjectDialog}
                                                disabled={isUnarchivingProject}
                                            >
                                                {isUnarchivingProject ? 'Unarchiving...' : 'Unarchive Project'}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {OVERVIEW_SECTIONS.filter((section) => visibleSections.includes(section.key)).map((section) => (
                        <div key={section.key} className={styles.categorySection}>
                            <div className={styles.categoryHeader}>{section.label}</div>
                            {section.key === 'orders' && <EckdatenOverviewOrders dataService={dataService} projektId={_project.projektID} />}
                            {section.key === 'projectTime' && <EckdatenOverviewProjectTime dataService={dataService} projektId={_project.projektID} />}
                            {section.key === 'financialData' && _project && <EckdatenOverviewFinancialData dataService={dataService} project={_project} />}
                            {section.key !== 'orders' && section.key !== 'projectTime' && section.key !== 'financialData' && (
                                <div className={styles.sectionPlaceholder}>{section.placeholder}</div>
                            )}
                        </div>
                    ))}

                    {/* Footer with Save/Cancel Buttons */}
                    {isAdmin && isEditing && (
                        <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, justifyContent: 'flex-start', paddingTop: tokens.spacingVerticalL, borderTop: `1px solid ${tokens.colorNeutralStroke2}`, marginTop: tokens.spacingVerticalL }}>
                            <Button
                                appearance="primary"
                                icon={<Save24Regular />}
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                                appearance="secondary"
                                icon={<Dismiss24Regular />}
                                onClick={handleCancel}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}

                    {/* Operational End Confirmation Dialog */}
                    <Dialog open={isCommandDialogOpen} onOpenChange={(_, data) => setIsCommandDialogOpen(data.open)}>
                        <DialogSurface>
                            <DialogBody>
                                <DialogTitle>{dialogConfig?.title}</DialogTitle>
                                <DialogContent>
                                    <p>{dialogConfig?.message}</p>
                                </DialogContent>
                                <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, justifyContent: 'center', paddingTop: tokens.spacingVerticalM }}>
                                    <Button
                                        appearance="secondary"
                                        onClick={() => setIsCommandDialogOpen(false)}
                                        disabled={isApprovingOffer || isTerminatingOperative || isTerminatingFinancial || isSettingStatus || isArchivingProject || isUnarchivingProject}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        appearance="primary"
                                        onClick={() => {
                                            if (dialogConfig?.actionType === 'approveOffer') {
                                                handleApproveOffer();
                                            } else if (dialogConfig?.actionType === 'operativeEnd') {
                                                confirmOperativeEnd();
                                            } else if (dialogConfig?.actionType === 'financialEnd') {
                                                confirmFinancialEnd();
                                            } else if (dialogConfig?.actionType === 'setStatusLaufend') {
                                                confirmSetStatus(4);
                                            } else if (dialogConfig?.actionType === 'setStatusOperativBeendet') {
                                                confirmSetStatus(5);
                                            } else if (dialogConfig?.actionType === 'archiveProject') {
                                                confirmArchiveProject();
                                            } else if (dialogConfig?.actionType === 'unarchiveProject') {
                                                confirmUnarchiveProject();
                                            }
                                        }}
                                        disabled={isApprovingOffer || isTerminatingOperative || isTerminatingFinancial || isSettingStatus || isArchivingProject || isUnarchivingProject}
                                    >
                                        {isApprovingOffer || isTerminatingOperative || isTerminatingFinancial || isSettingStatus || isArchivingProject || isUnarchivingProject ? 'Processing...' : 'Confirm'}
                                    </Button>
                                </div>
                            </DialogBody>
                        </DialogSurface>
                    </Dialog>
                </div>
                <aside className={styles.sidePanel} aria-label="Overview sections">
                    <div className={styles.sidePanelTitle}>Sections</div>
                    {OVERVIEW_SECTIONS.map((section) => (
                        <Checkbox
                            key={section.key}
                            label={section.label}
                            checked={visibleSections.includes(section.key)}
                            onChange={(_, data) => toggleSection(section.key, data.checked === true)}
                        />
                    ))}
                </aside>
            </div>
        </div>
    );
};

export default EckdatenOverview;
